import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to detect hardware barcode scanner input
 * 
 * USB/Bluetooth barcode scanners typically work as keyboard emulators.
 * They type the barcode very quickly (much faster than human typing)
 * and usually end with Enter key.
 * 
 * This hook detects rapid sequential key presses and captures them as a barcode.
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onScan - Callback when a barcode is scanned
 * @param {number} options.maxDelay - Max delay between keystrokes (default: 50ms)
 * @param {number} options.minLength - Minimum barcode length (default: 4)
 * @param {boolean} options.enabled - Whether scanner detection is enabled (default: true)
 * @param {string} options.targetInputId - If set, only trigger when this input is focused
 * @param {Array} options.excludeInputClasses - CSS classes to exclude from scanning (default: ['no-scanner'])
 * 
 * @returns {Object} - { lastScannedBarcode, isScanning, clearLastScan }
 */
const useBarcodeScanner = ({
  onScan,
  maxDelay = 50,
  minLength = 4,
  enabled = true,
  targetInputId = null,
  excludeInputClasses = ['no-scanner'],
} = {}) => {
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  
  const barcodeBuffer = useRef('');
  const lastKeyTime = useRef(0);
  const scanTimeout = useRef(null);

  const clearLastScan = useCallback(() => {
    setLastScannedBarcode('');
  }, []);

  const processScan = useCallback((barcode) => {
    if (barcode.length >= minLength) {
      setLastScannedBarcode(barcode);
      setIsScanning(false);
      
      if (onScan) {
        onScan(barcode);
      }
    }
    barcodeBuffer.current = '';
  }, [minLength, onScan]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      // Check if we should ignore this input
      const activeElement = document.activeElement;
      
      // If targeting a specific input, only process when that input is focused
      if (targetInputId) {
        if (!activeElement || activeElement.id !== targetInputId) {
          return;
        }
      }
      
      // Check for excluded classes
      if (activeElement && excludeInputClasses.some(cls => 
        activeElement.classList.contains(cls)
      )) {
        return;
      }

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime.current;

      // Clear buffer if too much time has passed (human typing)
      if (timeDiff > maxDelay && barcodeBuffer.current.length > 0) {
        barcodeBuffer.current = '';
        setIsScanning(false);
      }

      // Handle Enter key - process the barcode
      if (event.key === 'Enter') {
        if (barcodeBuffer.current.length >= minLength) {
          event.preventDefault();
          processScan(barcodeBuffer.current);
        }
        barcodeBuffer.current = '';
        return;
      }

      // Handle Tab key - some scanners use Tab as suffix
      if (event.key === 'Tab' && barcodeBuffer.current.length >= minLength) {
        event.preventDefault();
        processScan(barcodeBuffer.current);
        barcodeBuffer.current = '';
        return;
      }

      // Only accept printable characters
      if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // If this is rapid input (scanner-like), prevent default to avoid double input
        if (timeDiff < maxDelay && barcodeBuffer.current.length > 0) {
          // Don't prevent default - let it type into the input
          setIsScanning(true);
        }
        
        barcodeBuffer.current += event.key;
        lastKeyTime.current = currentTime;

        // Clear any existing timeout
        if (scanTimeout.current) {
          clearTimeout(scanTimeout.current);
        }

        // Set a timeout to process the barcode if Enter is not pressed
        scanTimeout.current = setTimeout(() => {
          if (barcodeBuffer.current.length >= minLength) {
            processScan(barcodeBuffer.current);
          } else {
            barcodeBuffer.current = '';
            setIsScanning(false);
          }
        }, 100);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      if (scanTimeout.current) {
        clearTimeout(scanTimeout.current);
      }
    };
  }, [enabled, maxDelay, minLength, targetInputId, excludeInputClasses, processScan]);

  return {
    lastScannedBarcode,
    isScanning,
    clearLastScan,
  };
};

export default useBarcodeScanner;
